import { disc, hash, px } from './art';
import type { PlanetId } from '../../content/planets';
import { SPACE } from './palette';

/**
 * Space art for transits, painted in code like the worlds: tiling starfields at three depths
 * (the near one streaks, it is light speed), lumpy asteroids and the destination planet.
 */

type Ctx = CanvasRenderingContext2D;

/**
 * One starfield layer that tiles horizontally. `streak` draws stars as horizontal lines that
 * long, for the fastest, nearest layer.
 */
export function paintStarfield(
  ctx: Ctx,
  width: number,
  height: number,
  count: number,
  salt: number,
  streak = 0,
): void {
  for (let i = 0; i < count; i++) {
    const x = Math.floor(hash(i, 1, salt) * width);
    const y = Math.floor(hash(i, 2, salt) * height);
    const roll = hash(i, 3, salt);
    const color = roll < 0.12 ? SPACE.starWarm : roll < 0.55 ? SPACE.starDim : SPACE.star;
    if (streak > 0) {
      const len = streak + Math.floor(hash(i, 4, salt) * streak);
      // Wrap the streak so the texture still tiles.
      for (let k = 0; k < len; k++) px(ctx, k < 2 ? SPACE.star : SPACE.streak, (x + k) % width, y);
    } else {
      px(ctx, color, x, y);
      if (roll > 0.97) {
        px(ctx, color, x - 1, y);
        px(ctx, color, x + 1, y);
        px(ctx, color, x, y - 1);
        px(ctx, color, x, y + 1);
      }
    }
  }
}

/** A faint nebula band across the back, so space isn't flat black. Tiles horizontally. */
export function paintNebula(ctx: Ctx, width: number, height: number): void {
  px(ctx, SPACE.void, 0, 0, width, height);
  for (let x = 0; x < width; x++) {
    const t = (x / width) * Math.PI * 2;
    const mid = height * 0.55 + Math.sin(t) * 30 + Math.sin(t * 3) * 10;
    const half = 34 + Math.sin(t * 2) * 14;
    for (let y = Math.round(mid - half); y < mid + half; y++) {
      // Dither toward the edges of the band.
      const edge = Math.abs(y - mid) / half;
      if (hash(x, y, 9) > edge * 1.1) px(ctx, SPACE.nebula, x, y);
    }
  }
}

/** An asteroid in a (2r + 2) square: a lumpy disc lit from the upper left, with craters. */
export function paintRock(ctx: Ctx, r: number, salt: number): void {
  const c = r + 1;
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      const angle = Math.atan2(y, x);
      const lump = r * (0.82 + 0.18 * hash(Math.round((angle + Math.PI) * 3), 0, salt));
      const d = Math.hypot(x, y);
      if (d > lump) continue;
      const lit = (-x - y) / (r * 1.4);
      const color = lit > 0.35 ? SPACE.rockLit : lit < -0.35 ? SPACE.rockDark : SPACE.rock;
      px(ctx, color, c + x, c + y);
    }
  }
  const craters = Math.max(1, Math.round(r / 5));
  for (let i = 0; i < craters; i++) {
    const cx = c + Math.round((hash(i, 5, salt) - 0.5) * r);
    const cy = c + Math.round((hash(i, 6, salt) - 0.5) * r);
    const cr = Math.max(1, Math.round(r / 5));
    disc(ctx, cx, cy, cr, SPACE.crater);
    px(ctx, SPACE.rockDark, cx - cr, cy, 1, 1);
  }
}

/** How a destination planet looks from space. */
export interface PlanetLook {
  /** Low ground (seas, maria). */
  readonly low: string;
  readonly high: string;
  readonly highLit: string;
  readonly shade: string;
  /** A glowing rim where there is air (none on the Moon). */
  readonly atmosphere: string | null;
  /** Share of the disc that is high ground. */
  readonly highShare: number;
  /** White polar caps (Mars). */
  readonly caps: boolean;
}

export const PLANET_LOOKS: Readonly<Record<PlanetId, PlanetLook>> = {
  moon: {
    low: '#5c5c6e',
    high: '#9a9aac',
    highLit: '#cfcfdc',
    shade: '#12121c',
    atmosphere: null,
    highShare: 0.6,
    caps: false,
  },
  mars: {
    low: '#8a3a22',
    high: '#c4643a',
    highLit: '#ea9464',
    shade: '#2a0e0a',
    atmosphere: '#e0a080',
    highShare: 0.5,
    caps: true,
  },
};

/** The destination planet: low and high ground, a rim where there is air, the night side. */
export function paintPlanet(ctx: Ctx, r: number, look: PlanetLook): void {
  const c = r + 3;
  if (look.atmosphere) disc(ctx, c, c, r + 2, look.atmosphere);
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      if (x * x + y * y > r * r) continue;
      const high = hash(Math.floor((x + r) / 5), Math.floor((y + r) / 4), 3) > 1 - look.highShare;
      const lit = (-x - y * 0.6) / r;
      let color: string = high ? (lit > 0.4 ? look.highLit : look.high) : look.low;
      if (look.caps && Math.abs(y) > r * 0.82) color = SPACE.star;
      // The night side, dithered at the terminator.
      if (lit < -0.45 || (lit < -0.25 && (x + y) % 2 === 0)) color = look.shade;
      px(ctx, color, c + x, c + y);
    }
  }
}
